import type { Appointment } from '../../api/timetable'
import { whatItIsAbout } from '../../api/timetable'
import { Button } from '../../ui/Control'
import { dayKey, dayNumber, readableTime, todayKey, weekdayOf } from './week'

/**
 * The timetable on a phone: the days one under another, as a list.
 *
 * **It is a component and no longer a stylesheet trick, and that is the point of the file.** The
 * week grid used to be stacked into a list by `timetable.css` — seven columns collapsed to one, the
 * hour rule hidden, every appointment's absolute position undone with `!important`. That works
 * exactly as far as the engine drawing it agrees about a grid whose columns have gone: what was
 * left on iOS was a strip a few points tall inside a box that would not scroll, with the day behind
 * it. A layout that only exists as the negation of another layout has no shape of its own to fall
 * back to.
 *
 * So a phone gets its own drawing, in ordinary flow — no absolute positions, no fixed heights, no
 * grid, nothing to scroll inside anything else. The page scrolls, which is the one gesture that
 * works everywhere.
 *
 * What it deliberately does *not* carry over from the grid:
 *
 * - **No drawing an appointment by dragging over the hours.** A 15-minute raster on 390 points is a
 *   gesture nobody lands; the control that adds one is right there on every day, and on a phone it
 *   is the better door rather than the second one.
 * - **No peek card.** A tap opens the appointment. On a wide screen the card saves leaving the week;
 *   here it is a hop between the tap and the thing somebody tapped.
 *
 * What it keeps is everything that says what the day *is*: the hours in the zone the lesson was
 * spoken in, the room, what it is about, and the marks for a cancelled or moved one.
 */
export interface AgendaProps {
  /** The days to show, in order. Seven for a week, one for a day. */
  readonly days: readonly string[]
  readonly appointments: readonly Appointment[]
  readonly openId: string | null
  readonly onOpen: (appointmentId: string) => void
  /** Add one on that day. The visible way in, and here the only one. */
  readonly onAdd: (day: string) => void
}

export function Agenda({ days, appointments, openId, onOpen, onAdd }: AgendaProps) {
  const today = todayKey()

  return (
    <div className="agenda">
      {days.map((day) => {
        const onThisDay = appointments.filter((one) => coversDay(one, day)).sort(byWhenTheyStart)

        return (
          <section className="agenda__day" key={day} data-today={day === today}>
            <header className="agenda__head">
              <h3 className="agenda__title">
                <span className="agenda__weekday">{weekdayOf(day)}</span>
                <span className="agenda__number">{dayNumber(day)}</span>
              </h3>
              {/* One per day, so "add something on Thursday" never needs a date field. */}
              <Button
                tone="bare"
                icon="plus"
                iconOnlyLabel={`Add an appointment on ${weekdayOf(day)} ${dayNumber(day)}`}
                onClick={() => {
                  onAdd(day)
                }}
              />
            </header>

            {onThisDay.length === 0 ? (
              // An empty day makes an offer rather than saying "no entries" — and on a week list
              // it is also what keeps the day visible at all.
              <p className="agenda__nothing">Nothing on.</p>
            ) : (
              <ul className="agenda__list">
                {onThisDay.map((one) => (
                  <li key={one.id}>
                    <button
                      type="button"
                      className="agenda__one"
                      data-open={one.id === openId}
                      data-cancelled={one.cancelled}
                      onClick={() => {
                        onOpen(one.id)
                      }}
                    >
                      <span className="agenda__when">{whenItIs(one, day)}</span>
                      <span className="agenda__what">
                        <span className="agenda__what-title">{one.title}</span>
                        {(one.room !== '' || whatItIsAbout(one) !== '') && (
                          <span className="agenda__what-detail">
                            {[whatItIsAbout(one), one.room]
                              .filter((part) => part !== '')
                              .join(' · ')}
                          </span>
                        )}
                        {one.cancelled && <span className="agenda__mark">Cancelled</span>}
                        {!one.cancelled && one.remark !== '' && (
                          <span className="agenda__mark agenda__mark--changed">{one.remark}</span>
                        )}
                        {one.goneFromSourceAt !== null && (
                          <span className="agenda__mark">
                            No longer offered by {one.origin.source}
                          </span>
                        )}
                        {one.writeupLine !== null && (
                          <span className="agenda__wrote">{one.writeupLine}</span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

/** Whether an appointment is on that day at all — a block session covers every day it runs over. */
function coversDay(appointment: Appointment, day: string): boolean {
  const from = dayKey(new Date(appointment.startsAt), appointment.zone)
  const until = dayKey(new Date(appointment.endsAt), appointment.zone)
  return from <= day && day <= until
}

/**
 * When it is, said in the zone it was spoken in.
 *
 * A day it merely runs *through* says so instead of showing 00:00–24:00, which would read as an
 * appointment somebody made rather than as the middle of a block session.
 */
function whenItIs(appointment: Appointment, day: string): string {
  if (appointment.allDay) {
    return 'All day'
  }
  const starts = new Date(appointment.startsAt)
  const ends = new Date(appointment.endsAt)
  const startsHere = dayKey(starts, appointment.zone) === day
  const endsHere = dayKey(ends, appointment.zone) === day

  if (startsHere && endsHere) {
    return `${readableTime(starts, appointment.zone)}–${readableTime(ends, appointment.zone)}`
  }
  if (startsHere) {
    return `from ${readableTime(starts, appointment.zone)}`
  }
  if (endsHere) {
    return `until ${readableTime(ends, appointment.zone)}`
  }
  return 'All day'
}

function byWhenTheyStart(one: Appointment, other: Appointment): number {
  if (one.allDay !== other.allDay) {
    return one.allDay ? -1 : 1
  }
  return one.startsAt.localeCompare(other.startsAt)
}
